import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Question } from "@phosphor-icons/react";

import Header from "@/components/Header";
import HeadlineTicker from "@/components/HeadlineTicker";
import UrlForm from "@/components/UrlForm";
import PipelineIndicator from "@/components/PipelineIndicator";
import VideoSidebar from "@/components/VideoSidebar";
import ClaimCard from "@/components/ClaimCard";
import VerdictReportCard from "@/components/VerdictReportCard";
import TrendingChecks from "@/components/TrendingChecks";
import FactCheckerToggle from "@/components/FactCheckerToggle";
import FreeQuotaModal from "@/components/FreeQuotaModal";
import HowItWorks from "@/components/HowItWorks";
import FAQs from "@/components/FAQs";
import SignupCTA from "@/components/SignupCTA";
import { isFactCheckerSource } from "@/lib/factCheckers";
import { anonQuotaExhausted, bumpAnonCount, anonQuotaRemaining, ANON_LIMIT } from "@/lib/anonQuota";
import { useAuth } from "@/context/AuthContext";
import useFactCheckStream from "@/hooks/useFactCheckStream";

export default function HomePage() {
  const { state, start, reset } = useFactCheckStream();
  const [factCheckersOnly, setFactCheckersOnly] = useState(false);
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const { user } = useAuth();

  const handleSubmit = useCallback((url) => {
    if (!user && anonQuotaExhausted()) {
      setQuotaModalOpen(true);
      return;
    }
    if (!user) bumpAnonCount();
    reset();
    setTimeout(() => start(url, false), 50);
  }, [start, reset, user]);

  // Build the ordered list of claim cards: prefer verdicts, fall back to skeleton claim placeholders
  const orderedClaims = useMemo(() => {
    const verdicts = state.verdictsByClaimId;
    // Use detected claims as ordering source
    const list = (state.claims || []).map((c) => {
      const v = verdicts[c.id];
      if (v) return v;
      return {
        claim_id: c.id,
        original_quote: c.original_quote,
        normalized_claim: c.normalized_claim,
        verdict: "UNVERIFIABLE",
        confidence: 0,
        sources: [],
        short_explanation: "",
        detailed_reasoning: "",
        timestamp_in_video: c.timestamp_in_video,
        priority: c.priority,
        _pending: true,
      };
    });
    // Also include any verdicts whose claim wasn't in detected list (edge case)
    Object.values(verdicts).forEach((v) => {
      if (!list.find((x) => x.claim_id === v.claim_id)) list.push(v);
    });
    return list;
  }, [state.claims, state.verdictsByClaimId]);

  const showResults = state.running || state.result || state.claims.length > 0 || state.metadata || state.error;

  const totalSourceCount = useMemo(
    () => orderedClaims.reduce((n, c) => n + (c.sources?.length || 0), 0),
    [orderedClaims]
  );
  const fcSourceCount = useMemo(
    () => orderedClaims.reduce(
      (n, c) => n + (c.sources || []).filter(isFactCheckerSource).length,
      0
    ),
    [orderedClaims]
  );

  // Toast on completion
  useEffect(() => {
    if (!state.running && state.result) {
      toast.success(`Fact-check complete · ${state.overallVerdict || "N/A"}`);
    }
  }, [state.running, state.result?.cache_key, state.overallVerdict]);

  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900 relative">
      <Header />
      <HeadlineTicker />

      {/* Hero + URL form — full when idle, compact bar after a check runs */}
      {!showResults ? (
        <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-14 md:pt-20 pb-8 relative z-10">
          <div className="max-w-4xl">
            <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-4">
              REAL-TIME MULTI-AGENT VERIFICATION · FIVE-STAGE PIPELINE
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-zinc-950 mb-6">
              Verify what your feed is telling you.
            </h1>
            <p className="text-lg md:text-xl text-zinc-700 leading-relaxed max-w-2xl mb-10">
              Paste a YouTube URL. Our five-agent pipeline transcribes the audio, extracts factual
              claims, cross-references authoritative sources, and delivers verdicts you can actually
              cite &mdash; usually in seconds.
            </p>
            <UrlForm onSubmit={handleSubmit} disabled={state.running} />
            {!user && (
              <div
                data-testid="anon-quota-badge"
                className="mt-4 font-mono-plex text-[10px] font-semibold text-zinc-500"
              >
                FREE FACT-CHECKS THIS BROWSER · {anonQuotaRemaining()}/{ANON_LIMIT} REMAINING
              </div>
            )}
          </div>
        </section>
      ) : (
        <section
          data-testid="compact-hero"
          className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pt-6 pb-4 relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        >
          <div className="flex items-baseline gap-3">
            <h1 className="font-display text-2xl md:text-3xl leading-none tracking-tight text-zinc-950">
              Fact-check
            </h1>
            <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
              {state.running ? "Streaming verdicts…" : "Verified"}
            </span>
          </div>
          <div className="w-full md:w-auto md:min-w-[440px]">
            <UrlForm onSubmit={handleSubmit} disabled={state.running} compact />
          </div>
        </section>
      )}

      {/* Pipeline status */}
      <AnimatePresence>
        {state.running && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            id="pipeline"
            className="max-w-[1600px] mx-auto w-full px-6 md:px-12 mb-4"
          >
            <PipelineIndicator
              stage={state.stage}
              message={state.stageMessage}
              active={state.running}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error banner (still shown alongside metadata if we have it) */}
      {state.error && (
        <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 mb-4">
          <div
            data-testid="error-banner"
            className="border-2 border-red-500 bg-red-50 text-red-900 px-4 py-3 flex items-start gap-3"
          >
            <span className="font-mono-plex text-[10px] font-semibold flex-shrink-0 mt-0.5">
              PIPELINE HALTED
            </span>
            <span className="text-[13px] font-medium">
              {state.error}
            </span>
          </div>
        </div>
      )}

      {/* Results grid */}
      {showResults && (
        <section className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <VideoSidebar
                metadata={state.metadata}
                videoUrl={state.result?.video_url}
                overallVerdict={state.overallVerdict}
                overallConfidence={state.overallConfidence}
                overallSummary={state.overallSummary}
                cached={state.cached}
                processingMs={state.processingMs}
                transcriptExcerpt={state.transcriptExcerpt}
                cacheKey={state.result?.cache_key}
              />
            </div>
          </div>

          <div className="lg:col-span-8 flex flex-col gap-6">
            {/* Verdict Report Card — top hero, above everything else */}
            {state.propaganda && state.metadata && (
              <VerdictReportCard
                propaganda={state.propaganda}
                factualAccuracy={state.factualAccuracy}
                trustScore={state.trustScore}
                claims={Object.values(state.verdictsByClaimId)}
                overallVerdict={state.overallVerdict}
                overallConfidence={state.overallConfidence}
                videoMetadata={state.metadata}
                cacheKey={state.result?.cache_key}
              />
            )}

            {orderedClaims.length === 0 && !state.running && state.metadata && (
              <div className="border border-zinc-300 bg-white p-8">
                <Question weight="duotone" className="w-8 h-8 text-zinc-500 mb-3" />
                <h3 className="font-display text-2xl text-zinc-950 mb-2">
                  No verifiable claims detected
                </h3>
                <p className="text-zinc-700">
                  Our claim detector didn&apos;t find any independently verifiable factual claims in
                  this video&apos;s transcript. Try a video with more explicit statistical,
                  scientific, or historical assertions.
                </p>
              </div>
            )}

            {orderedClaims.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 justify-between mb-1">
                <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 flex items-center gap-3">
                  <span>FINDINGS · {orderedClaims.length} CLAIM{orderedClaims.length === 1 ? "" : "S"}</span>
                  {state.running && (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full pulse-dot" />
                      STREAMING
                    </span>
                  )}
                </div>
                <FactCheckerToggle
                  enabled={factCheckersOnly}
                  onChange={setFactCheckersOnly}
                  matchCount={fcSourceCount}
                  totalCount={totalSourceCount}
                />
              </div>
            )}

            <AnimatePresence>
              {orderedClaims.map((c, i) => (
                <ClaimCard key={c.claim_id} claim={c} index={i} factCheckersOnly={factCheckersOnly} />
              ))}
            </AnimatePresence>
          </div>
        </section>
      )}

      {/* Trending */}
      {!showResults && (
        <>
          <HowItWorks />
          <section id="trending" className="max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-16">
            <TrendingChecks onSelect={handleSubmit} />
          </section>
          <SignupCTA />
          <FAQs />
        </>
      )}

      {/* Free-quota modal for anonymous users */}
      <FreeQuotaModal open={quotaModalOpen} onClose={() => setQuotaModalOpen(false)} />

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white/60 backdrop-blur mt-auto">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg text-zinc-950">TrueStream</div>
            <div className="font-mono-plex text-[10px] text-zinc-500 mt-1">
              VERIFY WHAT YOUR FEED IS TELLING YOU
            </div>
          </div>
          <div className="flex items-center gap-5 font-mono-plex text-[10px] font-semibold text-zinc-600">
            <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-zinc-950">PRIVACY</a>
            <a href="/terms" data-testid="footer-terms-link" className="hover:text-zinc-950">TERMS</a>
            <a href="/#faq" className="hover:text-zinc-950">FAQ</a>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-500">truestream.app</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
